/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';

import {
    pageContext,
    get
} from 'ctm-util-functions';

import { formAddOnUIComponent } from 'ctm-css-themes';

import { quoteFormSelector } from 'ctm-selectors';
import { connect } from 'react-redux';
import { SimpleFlexbox } from '../../flexbox/flexbox';

import { getResolvedConfigSchema } from './address_config_schema';
import { FieldRow } from '../containers/field_row/field_row';

import labelStyles from '../label/label.css';
import styles from '../containers/field_row/field_row.css';
import { resetHomeJourneyPayloadForAddress } from '../../prefill/utils';

class Address extends Component {

    constructor(props) {
        super(props);
        this.configSchema = getResolvedConfigSchema(props.schema.properties);

        this.formElementFactory = pageContext.getProps().factories.formElementFactory;
    }

    /**
     * Render a title of the Field row. Used for multi fields row. - moved from FieldRow
     * @returns {Node} - title header row that may contain a toggle
     */
    getTitle = () => {
        const { title } = this.configSchema;
        if (!title) {
            return null;
        }

        // title|label row addon width style
        const addonWidthClass = get(this.props, 'schema.field.addonWidth', 'default');
        const className = formAddOnUIComponent[`addon_width_${addonWidthClass}`];

        return (
            <SimpleFlexbox className={className} row gapless="xs" contentBetween="xs" itemsCenter="xs">
                <label className={labelStyles.variant_multiple_field}>
                    <span className={styles.multiple_field_heading}>{title}</span>
                </label>
            </SimpleFlexbox>
        );
    };

    /**
     * Return the element schema for the toggle element used for switching between autocomplete and MFA and vice-verse
     * @param {String} toggleType - The toggleType to render - autoCompleteToggle or mfaToggle
     * @returns {Object} - The element schema for the toggle element
     */
    getToggleElementSchema = (toggleType) => {
        const { dispatch } = this.props;
        const toggleElementSchema = this.configSchema[toggleType];
        const { onClick } = toggleElementSchema;
        const onClickFunc = () => {
            if (onClick.setState) {
                this.setState({ [onClick.setState.key]: onClick.setState.value });
            }
            if (onClick.actions) {
                onClick.actions.map((action) => dispatch(action));
            }
        };
        return {
            ...toggleElementSchema,
            onClick: onClickFunc
        };
    };

    /**
     * Return the Autocomplete component wrapped in a FieldRow
     * @returns {Node} - The Autocomplete component wrapped in a FieldRow
     */
    getAutoCompleteCombo = () => {
        const { selectedValues } = this.props;
        const schema = {
            ...this.configSchema.autoComplete,
            elements: [
                this.getToggleElementSchema('autoCompleteToggle')
            ]
        };
        return <FieldRow schema={schema} selectedValues={selectedValues} />;
    };

    /**
     * Return the MFA component wrapped in a FieldRow
     * @returns {Node} - The MFA component wrapped in a FieldRow
     */
    getMFA = () => {
        const { selectedValues } = this.props;
        const schema = {
            ...this.configSchema.mfa,
            elements: [
                this.getToggleElementSchema('mfaToggle')
            ]
        };
        return <FieldRow schema={schema} selectedValues={selectedValues} />;
    };

    componentDidUpdate(prevProps) {
        const { vertical, schema } = this.props;
        if (vertical === 'home-contents-insurance' && schema.identifier === 'property.address') {
            this.handleHomeAddressChange(prevProps);
        }
    }

    handleHomeAddressChange = (prevProps) => {
        const {
            dispatch,
            schema,
            gnafId,
            buildingName,
            unit,
            unitType,
            streetNumber,
            streetName,
            suburb,
            occupancyType,
            coverType,
            address,
            commencementDate,
            isOldestResidentRetired
        } = this.props;

        const prevGnaf = prevProps.gnafId;
        const currGnaf = gnafId;
        let addressChanged = false;
        if (prevGnaf && currGnaf && prevGnaf !== currGnaf) {
            addressChanged = true;
        } else {
            const prevAddress = [
                prevProps.buildingName || '',
                prevProps.unit || '',
                prevProps.unitType || '',
                prevProps.streetNumber || '',
                prevProps.streetName || '',
                prevProps.suburb || ''
            ].join(' ').trim();
            const currAddress = [
                buildingName || '',
                unit || '',
                unitType || '',
                streetNumber || '',
                streetName || '',
                suburb || ''
            ].join(' ').trim();
            if (prevAddress && prevAddress !== currAddress) {
                addressChanged = true;
            }
        }
        if (addressChanged) {
            resetHomeJourneyPayloadForAddress(dispatch, schema);
            this.dispatchSavedData(dispatch, address, occupancyType, coverType, commencementDate, isOldestResidentRetired);
        }
    };

    /**
     * Dispatches updated values to the Redux store for address and related fields.
     * This is called after an address change is detected, ensuring all relevant
     * quote form fields are updated in the store for downstream logic and UI.
     *
     * @param {function} dispatch - Redux dispatch function
     * @param {object} address - Address object to save
     * @param {string} occupancyType - Occupancy type value
     * @param {string} coverType - Cover type value
     * @param {string} commencementDate - Policy commencement date
     * @param {boolean} isOldestResidentRetired - Retirement status
     */
    dispatchSavedData = (dispatch, address, occupancyType, coverType, commencementDate, isOldestResidentRetired) => {
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'property',
            value: {
                yearBuilt: null,
                hasBodyCorporate: null,
                propertyType: null,
                wallMaterial: null,
                heritageOverlay: null,
                address
            }
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'occupancy.occupancyType',
            value: occupancyType
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'coverDetails.coverType',
            value: coverType
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'coverDetails.commencementDate',
            value: commencementDate
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'occupancy.isOldestResidentRetired',
            value: isOldestResidentRetired
        });
        // Clear rebuild cost calculator cached data when address changes to force recalculation on next load
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'rebuildCostCalculator.apiOnLoadResponse',
            value: null
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'rebuildCostCalculator.apiOnLoadError',
            value: null
        });
    };

    render = () => {
        return (
            <>
                {this.getTitle()}
                {this.getMFA()}
                {this.getAutoCompleteCombo()}
            </>
        );
    };

}

Address.propTypes = {
    schema: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    selectedValues: PropTypes.object,
    vertical: PropTypes.string,
    occupancyType: PropTypes.string,
    coverType: PropTypes.string,
    commencementDate: PropTypes.string,
    isOldestResidentRetired: PropTypes.bool,
    gnafId: PropTypes.string,
    buildingName: PropTypes.string,
    unit: PropTypes.string,
    unitType: PropTypes.string,
    streetName: PropTypes.string,
    streetNumber: PropTypes.string,
    suburb: PropTypes.string,
    address: PropTypes.object
};

const mapStateToProps = (state) => {
    const form = quoteFormSelector(state);
    return {
        vertical: state.getIn(['page', 'vertical']),

        // Selected Values
        occupancyType: form.getIn(['occupancy', 'occupancyType']),
        coverType: form.getIn(['coverDetails', 'coverType']),
        commencementDate: form.getIn(['coverDetails', 'commencementDate']),
        isOldestResidentRetired: form.getIn(['occupancy', 'isOldestResidentRetired']),

        // Lookup address
        address: form.getIn(['property', 'address']),
        gnafId: form.getIn(['property', 'address', 'gnafId']),

        // Manual address
        buildingName: form.getIn(['property', 'address', 'buildingName']),
        unit: form.getIn(['property', 'address', 'unitNumber']),
        unitType: form.getIn(['property', 'address', 'unitType']),
        streetName: form.getIn(['property', 'address', 'streetName']),
        streetNumber: form.getIn(['property', 'address', 'streetNumber']),
        suburb: form.getIn(['property', 'address', 'suburb'])
    };
};

const AddressConnected = connect(mapStateToProps)(Address);

export { Address };
export default AddressConnected;
